import OrderIdentifiers from '../OrderIdentifiers';
import React, { useState, useMemo } from 'react';
import {
  IconCamera,
  IconAlertTriangle,
  IconAlertCircle,
  IconPrinter,
  IconLock,
  IconCheck,
} from '@tabler/icons-react';
import { itemLabel, itemUom, trackingSummary } from '../../lib/requestItems';
import { packagesFor } from '../../lib/materialReceive';
import { getPrintOnReceiveDefault } from '../../lib/tenant';
import { useAuth } from '../AuthContext';
import { workOrderRefError, isValidWorkOrderRef, WORK_ORDER_REF_HINT } from '../../lib/workOrderRef';
import ActionButton from '../ActionButton';
import OnboardingCallout from '../OnboardingCallout';
import { qtyBox, QTY_BOX_CLASS } from '../../lib/qtyBox';
import QtyStepper from '../QtyStepper';

// Screen 8 of Issue #212 — receiving a delivery against a material request.
// Deliberately NOT a mode of ReceiveSheetContent.js: that component serves three
// ad-hoc stock-receipt call sites with no request context, and a goods receipt
// against a purchase document is a different transaction type in every enterprise
// system. Precedent: Session 71's #25 AddVendorSheet decision.
//
// Layout is authoritative from MOCKUP_212_SCREEN8_APPROVED.png; behavior from
// MOCKUP_REFERENCE_212.md Screen 8. Do not redesign either from this file.

const SHEET_PAD_X = 20;

export function outstandingFor(it) {
  const requested = Number(it?.qty_requested ?? 0);
  const received = Number(it?.qty_received ?? 0);
  const remaining = requested - received;
  return remaining > 0 ? remaining : 0;
}

export default function ReceiveDeliverySheetContent({
  request,              // full material_requests row + nested material_request_items
  vendorName,           // optional; falls back to request.inventory_vendors?.name
  branchName,
  canOverride,
  printOnReceiveDefault,
  printDescription,
  printAvailable,
  packs,                // #333 — Map<item_id, { packQty, packUnit, vendorName }>
  onSubmit,
  onCancel,
  saving,
  done,
  doneResult,           // jsonb returned by receive_material_request_items()
  serverError,
}) {
  const allItems = request?.material_request_items || [];

  // Only lines with something still owed appear on this sheet. A fully received
  // line has nothing left to check off.
  const openItems = useMemo(
    () => allItems.filter(it => outstandingFor(it) > 0),
    [allItems]
  );

  // Every row defaults to checked, including untracked rows — Colby's call,
  // Session 74k6: at D&R most orders carry no tracking at all, so an untracked
  // row is the normal case, not a suspicious one.
  const [checked, setChecked] = useState(() => {
    const init = {};
    openItems.forEach(it => { init[it.id] = true; });
    return init;
  });

  // Quantities default to what is still outstanding, never to what was originally
  // requested. A second delivery against a partly-filled line pre-fills only the
  // remainder (#235).
  const [qtys, setQtys] = useState(() => {
    const init = {};
    openItems.forEach(it => { init[it.id] = String(outstandingFor(it)); });
    return init;
  });

  const [jobRef, setJobRef] = useState(request?.work_order_ref ?? '');
  const [notes, setNotes] = useState('');
  const [packingSlip, setPackingSlip] = useState(null);
  const [confirmNoSlip, setConfirmNoSlip] = useState(false);
  const [localError, setLocalError] = useState('');
  // #342, Session 78c. Same defect and same fix as ReceiveSheetContent.js — this
  // was useState(printAvailable ? ... : false) and printAvailable is false at
  // mount on every caller, because RequestsInboxPage resolves the printer in a
  // useEffect. useState's initialiser runs once, so the default never applied.
  // null means untouched; the resolved default stays live until the receiver taps.
  const [printToggle, setPrintToggle] = useState(null);
  // #199 part 3, Session 79.22b.6.1f (approved drawing C) -- the same rule as
  // ReceiveSheetContent.js: with QR label printing off, nothing here offers or
  // starts a print, and printLabels is false for materialReceive.js.
  const labelPrintingEnabled = useAuth()?.labelPrintingEnabled !== false;
  const printLabels = printAvailable && labelPrintingEnabled
    ? (printToggle != null ? printToggle : (printOnReceiveDefault ?? getPrintOnReceiveDefault()))
    : false;

  const photoRef = React.useRef();
  const pdfRef = React.useRef();
  // #383, Session 78j — inv_receive_delivery_qty_first.
  //
  // Anchored to the instruction line rather than to a row's quantity field.
  // This sheet is multi-line, so there is no single "Qty received" label to
  // point at the way ReceiveSheetContent.js has one, and a row anchor is #38's
  // second failure mode: openItems is derived from what is still outstanding,
  // so the row this pointed at can vanish between sessions and the callout
  // would fail silently.
  const qtyIntroRef = React.useRef(null);

  const error = localError || serverError;

  const upstreamWoRef = request?.work_order_ref || null;

  // #502, Session 79.1 - a locked work order number the database will refuse is a
  // dead end for the one person standing at the pallet. material_requests carries
  // NO format rule while inventory_transactions does, so an order can arrive here
  // holding "WO-1234"; locked, that delivery could never be received by anybody
  // below manager. The lock applies only while the upstream value is valid.
  // See the fuller note in ReceiveSheetContent.js - both sheets or neither.
  const upstreamWoInvalid = !!upstreamWoRef && !isValidWorkOrderRef(upstreamWoRef);
  const isLocked = !!upstreamWoRef && !canOverride && !upstreamWoInvalid;
  const isAmber = !!upstreamWoRef && canOverride && !upstreamWoInvalid;

  // #499 - checked as they type, so the sheet refuses before the press instead of
  // the database refusing after it, invisibly. Empty stays valid; #277 made the
  // field optional deliberately.
  const jobRefError = workOrderRefError(jobRef);

  const resolvedVendor = vendorName || request?.inventory_vendors?.name || null;
  const summary = trackingSummary(allItems);

  const headerTitle = allItems.length > 0
    ? (allItems.length > 1
        ? `${itemLabel(allItems[0])} +${allItems.length - 1} more`
        : itemLabel(allItems[0]))
    : '—';

  function qtyNum(id) {
    const raw = qtys[id];
    if (raw === '' || raw == null) return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }

  function toggle(id) {
    setChecked(c => ({ ...c, [id]: !c[id] }));
  }

  function setQty(id, val) {
    setQtys(q => ({ ...q, [id]: val }));
  }

  // #333. Per-line label overrides, keyed by request_item_id. A line absent from
  // this map is still following its own quantity ÷ pack size, which is the state
  // every line opens in. Same override-rather-than-copy shape as the single-item
  // sheet, and for the same reason: the pack sizes arrive after mount, so a
  // seeded copy would freeze at the pre-fetch value.
  const [labelOverrides, setLabelOverrides] = useState({});

  // #333. What one line will actually print. Packages, not units.
  function packFor(it) {
    return packs?.get?.(it.item_id) || null;
  }
  function derivedLabelsFor(it) {
    if (!it.item_id) return 0;
    return packagesFor(qtyNum(it.id), packFor(it)?.packQty);
  }
  function labelsFor(it) {
    if (!it.item_id) return 0;
    const override = labelOverrides[it.id];
    return override != null ? override : derivedLabelsFor(it);
  }

  // Labels can only be printed for catalog items — an off-catalog line has no
  // inventory_items row, no SKU and no QR target to encode.
  //
  // #333: this total was the sum of received QUANTITIES, so a five-line delivery
  // carrying one pallet per line promised — and printed — hundreds of stickers.
  // It is now the sum of package counts, from the same per-line numbers the
  // dispatch loop uses.
  const labelCount = openItems.reduce((sum, it) => {
    if (!checked[it.id]) return sum;
    if (!it.item_id) return sum;
    return sum + labelsFor(it);
  }, 0);

  // An off-catalog request is always exactly one item — off-catalog was kept as its
  // own single-item request path by design so purchasing handles one at a time
  // (see ISSUES.md #224). So a request is either all catalog lines or one
  // off-catalog line, and a pure off-catalog delivery has nothing to label.
  const hasCatalogLines = openItems.some(it => !!it.item_id);

  const shortLines = openItems.filter(it => qtyNum(it.id) < outstandingFor(it) || !checked[it.id]);
  const anyChecked = openItems.some(it => checked[it.id] && qtyNum(it.id) > 0);

  let summaryText = null;
  if (!anyChecked) {
    summaryText = 'Nothing checked yet — check off what arrived in this delivery.';
  } else if (shortLines.length === 1) {
    const it = shortLines[0];
    const remaining = outstandingFor(it) - (checked[it.id] ? qtyNum(it.id) : 0);
    summaryText = `${itemLabel(it)} stays Ordered with ${remaining} outstanding.`;
  } else if (shortLines.length > 1) {
    summaryText = `${shortLines.length} items stay Ordered with quantities outstanding.`;
  }

  function handleSubmit() {
    const lines = openItems
      .filter(it => checked[it.id] && qtyNum(it.id) > 0)
      .map(it => ({
        request_item_id: it.id,
        item_id: it.item_id ?? null,
        qty: qtyNum(it.id),
        is_off_catalog: !!it.is_off_catalog,
        name: itemLabel(it),
        uom: itemUom(it),
        outstanding: outstandingFor(it),
        // #333. Travels with the line rather than as a sheet-level total,
        // because submitReceiveDelivery dispatches one print job per line and a
        // single number could not be split back out across items with different
        // pack sizes.
        labelCopies: labelsFor(it),
      }));

    if (lines.length === 0) {
      setLocalError('Check off at least one item and enter a quantity.');
      return;
    }
    const bad = lines.find(l => !Number.isFinite(l.qty) || l.qty <= 0);
    if (bad) {
      setLocalError(`Enter a valid quantity for ${bad.name}.`);
      return;
    }

    // #499 - a delivery rejected by the database showed the receiver nothing at
    // all: the sheet closed, no stock moved, and no message appeared. Refused here
    // instead, in the shop's own vocabulary.
    if (jobRefError) {
      setLocalError(jobRefError);
      return;
    }

    setLocalError('');

    // #277: same treatment as ReceiveSheetContent.js. A packing slip photo is
    // the preferred record of what arrived and is deliberately not required --
    // the CHECK constraint this replaced was satisfiable by one character in
    // any of three fields, so it bought a non-empty string rather than
    // documentation. Multi-item deliveries are the case where it matters most,
    // because a partial delivery is exactly what gets disputed with a vendor.
    if (!packingSlip && !confirmNoSlip) {
      setConfirmNoSlip(true);
      return;
    }
    onSubmit({
      lines,
      jobRef: jobRef.trim(),
      notes: notes.trim(),
      packingSlip,
      printLabels,
      labelCount,
    });
  }

  function attachSlip(file) {
    setPackingSlip(file || null);
    if (file) setConfirmNoSlip(false);
  }

  if (done) {
    const allComplete = doneResult?.all_complete !== false;
    const offCat = doneResult?.off_catalog_received || 0;
    // An off-catalog line records its received quantity on the order and posts
    // nothing to stock. Telling a receiver "inventory has been updated" after a
    // pure off-catalog delivery would be a false statement about where their
    // material went.
    const stockTouched = (doneResult?.lines_received || 0) > offCat;
    return (
      <>
        <div style={{ background: '#E8F5E9', borderRadius: 12, padding: 20, border: '1px solid #C8E6C9', marginBottom: 20, textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>✓</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#2E7D32', marginBottom: 4 }}>Delivery received</div>
          <div style={{ fontSize: 12, color: '#5C5C5C', lineHeight: 1.5 }}>
            {stockTouched ? 'Inventory has been updated. ' : ''}
            {allComplete
              ? 'This order is complete and has been closed.'
              : 'Some quantities are still outstanding, so this order stays open.'}
            {offCat > 0 && (stockTouched
              ? ' Off-catalog items were recorded on the order and flagged for catalog review.'
              : ' This item is not in the catalog, so no stock count changed. It has been recorded on the order and flagged for catalog review.')}
          </div>
        </div>
        {/* #497 - the delivery is recorded and the photo is not, said in that order.
            The old behaviour threw the whole goods receipt away - the settlement of
            a purchase order, not just a count - and showed a message naming only the
            photo, which reads as "the delivery went in". */}
        {doneResult?.photoFailed && (
          <div style={{ background: '#FAEEDA', border: '1.5px solid #EF9F27', borderRadius: 12, padding: '12px 14px', marginBottom: 20, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <IconAlertTriangle size={16} color="#633806" style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#633806' }}>The packing slip photo did not upload</div>
              <div style={{ fontSize: 11, color: '#633806', marginTop: 2 }}>
                The delivery is recorded and the order has been updated - do not receive it
                again. Only the photo is missing. Keep the paper slip and hand it to your manager.
              </div>
            </div>
          </div>
        )}
        <button onClick={onCancel} style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: '#1D9E75', color: 'white', cursor: 'pointer' }}>Done</button>
      </>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 500, color: '#0F0F0F', marginBottom: 2 }}>Receive delivery</div>
          <div style={{ fontSize: 12, color: '#7A7A7A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{headerTitle}</div>
        </div>
      </div>

      {/* #304 D16 / #545, Session 79.14d. This corner held the PO number only
          when there WAS one, and the request number, and nothing else — so the
          header changed shape between two deliveries and showed two of six.
          The block below carries all of them, dashes included, and moving it
          out of the right-hand corner is what makes room for that.

          The person reading this is at a pallet with a paper packing slip in
          their hand; the number on that slip is as likely to be the vendor's
          own reference as ours. */}
      <OrderIdentifiers req={request} title="This order" style={{ marginBottom: 14 }} />

      <div style={{ background: '#E6F1FB', border: '1px solid #B5D4F4', borderRadius: 8, padding: '10px 12px', marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: '#0C447C' }}>
          Open order{resolvedVendor ? ` · ${resolvedVendor}` : ''}
        </div>
        <div style={{ fontSize: 11, color: '#0C447C', marginTop: 1 }}>
          {allItems.length} item{allItems.length !== 1 ? 's' : ''} ordered · {summary.tracked} of {summary.total} tracked
        </div>
      </div>

      <div ref={qtyIntroRef} style={{ fontSize: 12, color: '#5C5C5C', lineHeight: 1.5, marginBottom: 6 }}>
        Check off what arrived in this delivery. Quantities default to what's still outstanding — edit if this shipment is partial.
      </div>

      {error && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{error}</div>}

      <div style={{ marginBottom: 14 }}>
        {openItems.map(it => {
          const outstanding = outstandingFor(it);
          const isOn = !!checked[it.id];
          const q = qtyNum(it.id);
          const isShort = isOn && q > 0 && q < outstanding;
          const isOver = isOn && q > outstanding;
          const uom = itemUom(it);

          return (
            <div
              key={it.id}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '13px 0',
                borderBottom: '0.5px solid #F0F0F0',
                background: isOver ? '#FCEBEB' : 'transparent',
                marginLeft: isOver ? -SHEET_PAD_X : 0,
                marginRight: isOver ? -SHEET_PAD_X : 0,
                paddingLeft: isOver ? SHEET_PAD_X : 0,
                paddingRight: isOver ? SHEET_PAD_X : 0,
                opacity: isOn ? 1 : 0.45,
              }}
            >
              <button
                onClick={() => toggle(it.id)}
                aria-label={isOn ? `Uncheck ${itemLabel(it)}` : `Check ${itemLabel(it)}`}
                style={{
                  width: 21, height: 21, borderRadius: 5, flexShrink: 0, marginTop: 2,
                  background: isOn ? '#1D9E75' : 'transparent',
                  border: isOn ? 'none' : '1.5px solid #C0C0C0',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', padding: 0,
                }}
              >
                {isOn && <IconCheck size={14} color="white" stroke={3} />}
              </button>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: '#0F0F0F' }}>{itemLabel(it)}</div>
                <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 1 }}>
                  {outstanding}{uom ? ` ${uom}` : ''} outstanding
                  {it.tracking_number ? ` · ${it.tracking_carrier ? it.tracking_carrier + ' ' : ''}${it.tracking_number}` : ''}
                </div>

                {isShort && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#9A5F00', marginTop: 4 }}>
                    <IconAlertTriangle size={12} color="#BA7517" />
                    Short {outstanding - q} — stays ordered
                  </div>
                )}
                {isOver && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#A32D2D', marginTop: 4 }}>
                    <IconAlertCircle size={12} color="#A32D2D" />
                    {q - outstanding} over ordered quantity — managers notified
                  </div>
                )}
                {it.is_off_catalog && !it.item_id && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#9A5F00', marginTop: 4 }}>
                    <span style={{ fontSize: 11 }}>✨</span>
                    Off-catalog — flagged for catalog review
                  </div>
                )}

                {/* #333. The label-count override, rendered ONLY on lines where
                    the package count can differ from the quantity — a vendor
                    pack size above 1. Every other line prints one label per unit
                    exactly as it always has, and giving all of them a second
                    stepper would double the height of a sheet whose whole job is
                    checking off five lines on a phone.

                    The gate drew this control on the single-item sheet only. It
                    is applied here because the delivery sheet has the identical
                    defect — a line for a pallet of fifty printed fifty labels —
                    and shipping the fix on one of the two receive paths would
                    leave the goods-receipt path, the one Dallas uses against a
                    purchase order, still wrong. */}
                {isOn && printAvailable && labelPrintingEnabled && it.item_id && packFor(it)?.packQty > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.04em' }}>Labels</span>
                    {/* #760 - the shared stepper (approved drawing, section 2):
                        26 to 40 with a finger, and the count is now a box you
                        can type in. An emptied box is 0 labels. */}
                    <div>
                      <QtyStepper value={String(labelsFor(it))} min={0}
                        label={`Labels for ${itemLabel(it)}`}
                        fewerLabel={`One fewer label for ${itemLabel(it)}`}
                        moreLabel={`One more label for ${itemLabel(it)}`}
                        onChange={v => setLabelOverrides(o => ({ ...o, [it.id]: v === '' ? 0 : parseInt(v, 10) }))}
                        boxStyle={{ flex: 'none', width: 52, fontFamily: 'var(--font-body)', fontSize: 16, letterSpacing: 'normal', color: '#0F0F0F' }} />
                    </div>
                    <span style={{ fontSize: 11, color: '#7A7A7A' }}>
                      {q} {uom || 'units'} ÷ {packFor(it).packQty} per {packFor(it).packUnit || 'pack'}
                      {labelOverrides[it.id] != null && labelOverrides[it.id] !== derivedLabelsFor(it)
                        ? ` · overridden from ${derivedLabelsFor(it)}`
                        : ''}
                    </span>
                  </div>
                )}
              </div>

              {isOn && (
                <input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  value={qtys[it.id] ?? ''}
                  onFocus={e => e.target.select()}
                  onChange={e => setQty(it.id, e.target.value)}
                  // #708 -- the count this receipt confirms, in the shared
                  // confirm-box style; over and short keep their own warning
                  // colours on top, because those are the answer to read first.
                  className={QTY_BOX_CLASS}
                  style={{
                    ...qtyBox({ width: 72, align: 'center' }), flexShrink: 0,
                    ...(isOver ? { color: '#A32D2D', background: '#FCEBEB', border: '2px solid #E24B4A' }
                      : isShort ? { color: '#633806', background: '#FAEEDA', border: '2px solid #EF9F27' } : {}),
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {summaryText && (
        <div style={{ background: '#FAEEDA', border: '1px solid #EF9F27', borderRadius: 8, padding: '10px 12px', marginBottom: 16 }}>
          <span style={{ fontSize: 12, color: '#633806' }}>{summaryText}</span>
        </div>
      )}

      <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 6 }}>
        WO# <span style={{ fontWeight: 400, textTransform: 'none', color: '#9A9A9A' }}>(optional)</span>
      </div>
      <input
        type="text"
        value={jobRef}
        onChange={e => !isLocked && setJobRef(e.target.value)}
        readOnly={isLocked}
        placeholder="e.g. 123456 or 123456-7"
        style={{
          width: '100%', height: 40, borderRadius: 10, padding: '0 12px',
          fontSize: 16, color: isLocked ? '#7A7A7A' : '#0F0F0F',
          background: isLocked ? '#F5F5F3' : isAmber ? '#FAEEDA' : 'white',
          border: jobRefError ? '1.5px solid #A32D2D' : isAmber ? '1.5px solid #EF9F27' : '1px solid #E0E0E0',
          marginBottom: 4, boxSizing: 'border-box',
        }}
      />
      {/* #499 - beside the field being typed in, not at the top of a long sheet. */}
      {jobRefError && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4, fontSize: 11, color: '#A32D2D', marginBottom: 16 }}>
          <IconAlertTriangle size={11} color="#A32D2D" style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{WORK_ORDER_REF_HINT}</span>
        </div>
      )}
      {/* #502 - the number on the order cannot be saved, so it is editable here. */}
      {upstreamWoInvalid && !jobRefError && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4, fontSize: 11, color: '#9A5F00', marginBottom: 16 }}>
          <IconAlertTriangle size={11} color="#BA7517" style={{ flexShrink: 0, marginTop: 1 }} />
          <span>The work order number on this order ("{upstreamWoRef}") is not a valid one, so you can correct it here.</span>
        </div>
      )}
      {isLocked && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#7A7A7A', marginBottom: 16 }}>
          <IconLock size={11} />
          Set upstream — read only for your role
        </div>
      )}
      {isAmber && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#9A5F00', marginBottom: 16 }}>
          <IconAlertTriangle size={11} color="#BA7517" />
          Overriding locked value — change will be logged
        </div>
      )}
      {!isLocked && !isAmber && !jobRefError && !upstreamWoInvalid && <div style={{ marginBottom: 16 }} />}

      <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 6 }}>
        Notes <span style={{ fontWeight: 400, textTransform: 'none', color: '#9A9A9A' }}>(optional)</span>
      </div>
      <input
        type="text"
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="e.g. partial shipment, 2 boxes damaged"
        style={{ width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 10, padding: '0 12px', fontSize: 16, color: '#0F0F0F', marginBottom: 16, boxSizing: 'border-box' }}
      />

      <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 6 }}>
        Packing slip <span style={{ fontWeight: 400, textTransform: 'none', color: '#9A9A9A' }}>(optional)</span>
      </div>
      <input ref={photoRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => attachSlip(e.target.files[0])} />
      <input ref={pdfRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={e => attachSlip(e.target.files[0])} />
      <button onClick={() => photoRef.current.click()} style={{ width: '100%', padding: '12px 0', border: '1px dashed #C0C0C0', borderRadius: 10, background: '#F8F8F8', fontSize: 13, color: '#5C5C5C', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 6 }}>
        <IconCamera size={16} color="#7A7A7A" />
        Take photo
      </button>
      <button onClick={() => pdfRef.current.click()} style={{ width: '100%', background: 'none', border: 'none', fontSize: 11, color: '#9A9A9A', cursor: 'pointer', padding: '2px 0', marginBottom: 4, textDecoration: 'underline' }}>
        or attach a PDF
      </button>
      {packingSlip && (
        <div style={{ fontSize: 11, color: '#5C5C5C', marginBottom: 8, textAlign: 'center' }}>
          Attached: {packingSlip.name}
        </div>
      )}
      {!packingSlip && !confirmNoSlip && (
        <div style={{ background: '#FAEEDA', border: '1px solid #EF9F27', borderRadius: 8, padding: '8px 12px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconAlertTriangle size={14} color="#633806" style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 11, color: '#633806' }}>No packing slip attached — a photo is the preferred record</span>
        </div>
      )}

      {hasCatalogLines && labelPrintingEnabled && (
      <button
        onClick={() => printAvailable && setPrintToggle(!printLabels)}
        disabled={!printAvailable}
        style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${!printAvailable ? '#E0E0E0' : printLabels ? '#9FE1CB' : '#E0E0E0'}`, background: !printAvailable ? '#F5F5F3' : printLabels ? '#E8F8F2' : '#F8F8F8', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 16, cursor: printAvailable ? 'pointer' : 'not-allowed', opacity: printAvailable ? 1 : 0.6 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconPrinter size={16} color={!printAvailable ? '#C0C0C0' : printLabels ? '#1D9E75' : '#9A9A9A'} />
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: !printAvailable ? '#9A9A9A' : printLabels ? '#085041' : '#7A7A7A' }}>
              {!printAvailable
                ? 'Printing unavailable'
                : printLabels
                  ? (labelCount > 0
                      ? `Print ${labelCount} label${labelCount !== 1 ? 's' : ''} after confirming`
                      : 'Print labels after confirming')
                  : 'Print labels after confirming'}
            </div>
            <div style={{ fontSize: 11, color: !printAvailable ? '#9A9A9A' : printLabels ? '#0F6E56' : '#9A9A9A' }}>
              {printAvailable ? (printLabels ? printDescription : 'Tap to enable') : printDescription}
            </div>
          </div>
        </div>
        <div style={{ width: 36, height: 22, borderRadius: 11, background: !printAvailable ? '#E0E0E0' : printLabels ? '#1D9E75' : '#D0D0D0', position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}>
          <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'white', position: 'absolute', top: 2, left: printAvailable && printLabels ? 16 : 2, transition: 'left 0.2s' }} />
        </div>
      </button>
      )}

      {confirmNoSlip && !packingSlip && (
        <div style={{ background: '#FAEEDA', border: '1.5px solid #EF9F27', borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
            <IconAlertTriangle size={16} color="#633806" style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#633806' }}>Receive without a packing slip photo?</div>
              <div style={{ fontSize: 11, color: '#633806', marginTop: 2 }}>
                A photo is the preferred record of what actually arrived. You can still receive without one.
              </div>
            </div>
          </div>
          <button
            onClick={() => photoRef.current.click()}
            style={{ width: '100%', padding: 12, borderRadius: 10, border: 'none', background: '#EF9F27', color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}
          >
            <IconCamera size={16} color="white" />
            Take the photo
          </button>
          {/* #499/#503 - blocked, not dead. See components/ActionButton.js. */}
          <ActionButton
            onClick={handleSubmit}
            blockedReason={jobRefError}
            reasonAlreadyShown
            busy={saving}
            busyLabel="Saving..."
            style={{ width: '100%', background: 'none', border: 'none', fontSize: 12, color: '#633806', cursor: 'pointer', padding: 4, textDecoration: 'underline' }}
          >
            Receive without it
          </ActionButton>
        </div>
      )}

      {!(confirmNoSlip && !packingSlip) && (
        <ActionButton
          onClick={handleSubmit}
          blockedReason={jobRefError}
          reasonAlreadyShown
          busy={saving}
          busyLabel="Saving..."
          style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 500, border: 'none', background: '#1D9E75', color: 'white', cursor: 'pointer', marginBottom: 10 }}
        >
          {jobRefError ? 'Check the WO# above' : 'Confirm receipt'}
        </ActionButton>
      )}
      <button onClick={onCancel} style={{ width: '100%', background: 'none', border: 'none', fontSize: 13, color: '#7A7A7A', cursor: 'pointer', padding: 4 }}>Cancel</button>

      {/* #383, Session 78j. Receiving the wrong quantity is the highest-
          consequence wrong action in the module, and it was explained on only
          one of the two surfaces that can do it: inv_receive_qty_first went into
          ReceiveSheetContent.js at 78h, which serves the action catalog and both
          item-detail paths, while the Requests inbox uses this deliberately
          separate component for multi-line deliveries.

          This is the SECOND callout on this screen -- inv_inbox_tabs_first is
          already on the page behind it. Gate decision D3 allows one per screen;
          Colby ruled two here, on the argument that preventing a wrong receive
          beats explaining a read-only tab.

          Shown only when there is more than one line. On a single-line delivery
          the sheet above already reads as the one-item sheet does, and a tip
          about "each line" describing one line is the tip-without-its-subject
          that D4 forbids. */}
      <OnboardingCallout
        calloutKey="inv_receive_delivery_qty_first"
        text="Each line defaults to what's still owed. Change any that arrived short — the rest of the order stays open."
        anchorRef={qtyIntroRef}
        show={openItems.length > 1}
      />
    </>
  );
}
